import { useEffect, useRef } from "react";
import { useAPI } from "@huub-web/backend";

import type {
  Photo,
  TreeNode,
  Classification,
  InteriorDesignerAction,
  BackendGenerationAction,
} from "../context/types";
import { mapBackendActionsToFrontend } from "../utils/mapBackendActions";
import {
  mapBackendSuggestions,
  type BackendPhotoSuggestion,
} from "../utils/mapBackendSuggestions";

/**
 * Backend GET /interior-designer/photo/{id} response shape.
 * Loose typing — this is a dev integration hook, not a production contract.
 */
export type BackendGetPhoto = {
  id: string;
  origin: "funda" | "generated" | "upload";
  source_url: string | null;
  blob_url: string | null;
  thumbnail_url: string | null;
  parent_photo_id: string | null;
  actions: BackendGenerationAction[] | null;
  room_type: string | null;
  room_condition: "shell" | "dated" | "good" | null;
  furnished: boolean | null;
  inside_or_outside: "in" | "out" | null;
  deleted_at: string | null;
  created: string;
  updated: string;
};

export type BackendTreeNode = {
  photo: BackendGetPhoto;
  children: BackendTreeNode[];
};

type BackendPhotoWithSuggestions = {
  photo: BackendGetPhoto;
  tree: BackendTreeNode | null;
  suggestions: BackendPhotoSuggestion[];
};

export function mapClassification({
  photo,
}: {
  photo: {
    room_type: string | null;
    room_condition: "shell" | "dated" | "good" | null;
    furnished: boolean | null;
    inside_or_outside: "in" | "out" | null;
  };
}): Classification {
  return {
    room_type: photo.room_type,
    room_condition: photo.room_condition,
    furnished: photo.furnished,
    inside_or_outside: photo.inside_or_outside,
  };
}

function mapPhoto({ photo }: { photo: BackendGetPhoto }): Photo {
  const imageUrl = photo.blob_url ?? photo.source_url ?? "";
  return {
    id: String(photo.id),
    origin: photo.origin,
    blob_url: imageUrl,
    thumbnail_url: photo.thumbnail_url ?? imageUrl,
    parent_photo_id: photo.parent_photo_id
      ? String(photo.parent_photo_id)
      : null,
    actions: mapBackendActionsToFrontend({
      actions: photo.actions,
    }),
    classification: mapClassification({ photo }),
    deleted_at: photo.deleted_at,
    created_at: photo.created,
  };
}

/**
 * Flattens the backend's recursive TreeNode into a flat array
 * matching the frontend TreeNode[] shape.
 */
export function flattenTree({ node }: { node: BackendTreeNode }): TreeNode[] {
  const mapped = mapPhoto({ photo: node.photo });
  const self: TreeNode = {
    id: mapped.id,
    parent_photo_id: mapped.parent_photo_id,
    origin: mapped.origin,
    thumbnail_url: mapped.thumbnail_url,
    actions: mapped.actions,
    classification: mapped.classification,
    created_at: mapped.created_at,
    deleted_at: mapped.deleted_at,
  };

  const result: TreeNode[] = [self];
  for (const child of node.children) {
    result.push(...flattenTree({ node: child }));
  }
  return result;
}

/**
 * Fetches a real photo from the backend and dispatches PHOTO_LOADED.
 * Maps backend suggestion observations to frontend panel-level suggestions
 * via mapBackendSuggestions.
 */
export function usePhotoLoader({
  photoId,
  dispatch,
}: {
  photoId: string;
  dispatch: (action: InteriorDesignerAction) => void;
}) {
  const { $get } = useAPI();
  const $getRef = useRef($get);
  $getRef.current = $get;
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    $getRef
      .current(`/interior-designer/photo/${photoId}`)
      .then((response: { data: BackendPhotoWithSuggestions }) => {
        const { data } = response;
        const photo = mapPhoto({ photo: data.photo });
        const tree = data.tree ? flattenTree({ node: data.tree }) : [];

        dispatch({
          type: "PHOTO_LOADED",
          photo,
          tree,
          suggestions: mapBackendSuggestions({
            observations: data.suggestions,
          }),
        });
      })
      .catch((error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        const detail = (error as { response?: { data?: { detail?: string } } })
          ?.response?.data?.detail;
        console.error(
          `[usePhotoLoader] Failed to load photo (HTTP ${status}):`,
          detail ?? error,
        );
      });
  }, [photoId, dispatch]);
}
